import React, { forwardRef, useState, useEffect } from 'react'
import CurrencyInput from '@ericblade/react-currency-input'
import styled, { css } from 'styled-components'

import { colors } from 'styles/colors'

const FloatLabel = styled.label`
    position: absolute;
    background-color: white;
    border-radius: 15px;
    color: #282828;
    cursor: text;
    margin-left: 9px;
    margin-bottom: 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 90%;
    padding: 0 4px;
    left: 0;
    top: 50%;
    -webkit-transition: all 200ms;
    transition: all 200ms;
    pointer-events: none;
    transform: translateY(-50%);
    font-size: 15px;
`

const UpLabel = css`
    color: ${colors.darkGreen};
    font-size: 13px;
    transform: translate3d(0, -50%, 0);
    top: 0;
`

const InputStyle = css`
    flex: 1 1;
    width: 100%;
    border: none;
    background-color: transparent;
    padding: 13px 0 13px 13px;
    border-radius: 5px;
    font-size: 15px;
    font-weight: 300;
    color: #282828;

    &::-ms-clear {
        display: none;
    }

    &:disabled {
        border-color: ${colors.osloGray};
        color: ${colors.osloGray};

        + ${FloatLabel} {
            color: ${colors.osloGray} !important;
        }
    }

    &:focus + ${FloatLabel} {
        ${UpLabel}
    }

    ${(p) =>
        (p.hasValue || p.value || p.defaultValue) &&
        css`
            & + ${FloatLabel} {
                ${UpLabel}
            }
        `}

    &:not(:placeholder-shown) + ${FloatLabel},
  &:-webkit-autofill + ${FloatLabel} {
    }

    &:focus,
    &:valid {
        text-overflow: ellipsis;
        outline: none;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
        -webkit-text-fill-color: ${colors.darkGreen} !important;
        -webkit-box-shadow: 0 0 0 30px ${colors.smoothGray} inset !important;
        box-shadow: 0 0 0 30px ${colors.smoothGray} inset !important;
        font-family:
            'Museo Sans',
            -apple-system,
            BlinkMacSystemFont,
            'Segoe UI',
            'Roboto',
            sans-serif;
        -webkit-font-smoothing: antialiased;
    }
`

const StyledInput = styled.input`
    ${InputStyle}
`

const StyledCurrencyInput = styled(CurrencyInput).attrs((p) => ({
    onChangeEvent: p.onChange
}))`
    ${InputStyle}
`

const Container = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    border-radius: 5px;
    box-sizing: border-box;
    line-height: 20px;
    outline: none;
    transition: border 200ms;
    width: 100%;
    padding-right: 11px;
    background-color: #f5f4f4;

    border: 1px solid
        ${({ hasError, errorColor }) => {
            if (hasError) {
                if (errorColor) return errorColor
                return '#C6487E'
            }
            return '#e5e7eb'
        }};

    &:focus-within {
        border-color: ${({ hasError, errorColor }) => {
            if (hasError) {
                if (errorColor) return errorColor
                return '#C6487E'
            }
            return colors.aleloGreen
        }};

        box-shadow: 0 0 2px
            ${({ hasError, errorColor }) => {
                if (hasError) {
                    if (errorColor) return errorColor
                    return '#C6487E'
                }
                return colors.aleloGreen
            }};
        background-color: white;
    }
`

const Input = forwardRef(
    (
        {
            id,
            className,
            label,
            children,
            isCurrency,
            hasError = false,
            onChange = () => {},
            isAlwaysOpen = false,
            ...otherProps
        },
        ref
    ) => {
        const [hasValue, setHasValue] = useState(isAlwaysOpen)

        useEffect(() => {
            if (['', null, undefined].includes(otherProps.value)) {
                setHasValue(false)
            }
        }, [otherProps, setHasValue])

        const InputComp = isCurrency ? StyledCurrencyInput : StyledInput

        const handleInput = (e) => setHasValue(!!e.target.value)
        const handleChange = (e, mask, value) =>
            onChange(isCurrency ? value : e.target.value)

        return (
            <Container
                hasError={hasError}
                errorColor={otherProps.errorColor}
                className={className}
            >
                <InputComp
                    id={id}
                    ref={ref}
                    hasValue={hasValue}
                    onInput={handleInput}
                    onChange={handleChange}
                    {...otherProps}
                />
                {label && <FloatLabel htmlFor={id}>{label}</FloatLabel>}
                {children}
            </Container>
        )
    }
)
Input.displayName = 'Input'

export default Input
